import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Home, Palmtree, PartyPopper } from "lucide-react";
import { format, parseISO, isToday, isTomorrow, differenceInCalendarDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useReportTileEmpty } from "./DashboardTile";

interface TimeOffItem {
  kind: 'leave' | 'wfh' | 'holiday';
  id: string;
  userId: number | null;
  userName: string | null;
  isMe: boolean;
  startDate: string;
  endDate: string;
  label: string;
  color?: string | null;
}

interface UpcomingTimeOffResponse {
  items: TimeOffItem[];
  windowDays: number;
}

const KIND_META: Record<TimeOffItem['kind'], { icon: typeof Palmtree; iconClass: string; bgClass: string }> = {
  leave: {
    icon: Palmtree,
    iconClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-50 dark:bg-amber-950/30',
  },
  wfh: {
    icon: Home,
    iconClass: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-50 dark:bg-blue-950/30',
  },
  holiday: {
    icon: PartyPopper,
    iconClass: 'text-fuchsia-600 dark:text-fuchsia-400',
    bgClass: 'bg-fuchsia-50 dark:bg-fuchsia-950/30',
  },
};

function formatDayLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  const diff = differenceInCalendarDays(date, new Date());
  if (diff > 0 && diff < 7) return format(date, 'EEEE');
  return format(date, 'EEE, MMM d');
}

function formatRange(start: string, end: string): string {
  if (start === end) return formatDayLabel(start);
  const startLabel = formatDayLabel(start);
  const endDate = parseISO(end);
  return `${startLabel} – ${format(endDate, 'MMM d')}`;
}

export default function UpcomingTimeOffWidget() {
  const { data, isLoading } = useQuery<UpcomingTimeOffResponse>({
    queryKey: ["/api/dashboard/upcoming-time-off"],
  });

  const items = data?.items ?? [];

  const grouped = useMemo(() => {
    const map = new Map<string, TimeOffItem[]>();
    for (const item of items) {
      const key = item.startDate;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries()).map(([date, list]) => ({ date, list }));
  }, [items]);

  useReportTileEmpty("upcoming_time_off", !isLoading && (grouped?.length ?? 0) === 0);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-md">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Upcoming Time Off</CardTitle>
          </div>
          {data && (
            <span className="text-xs text-muted-foreground font-medium">Next {data.windowDays} days</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">All clear for the next 2 weeks</p>
            <p className="text-xs text-muted-foreground mt-1">No leave, WFH, or holidays scheduled</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {grouped.map(({ date, list }) => (
              <div key={date}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  {formatDayLabel(date)}
                </div>
                <div className="space-y-1.5">
                  {list.map((item) => {
                    const meta = KIND_META[item.kind];
                    const Icon = meta.icon;
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2",
                          meta.bgClass,
                        )}
                      >
                        <Icon className={cn("h-4 w-4 shrink-0", meta.iconClass)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium text-foreground truncate">
                            {item.kind === 'holiday' ? (
                              <span className="truncate">{item.label}</span>
                            ) : (
                              <>
                                <span className="truncate">
                                  {item.isMe ? 'You' : item.userName}
                                </span>
                                <span className="text-muted-foreground font-normal">·</span>
                                <span className="text-muted-foreground font-normal truncate">{item.label}</span>
                              </>
                            )}
                          </div>
                          {item.startDate !== item.endDate && (
                            <div className="text-xs text-muted-foreground">
                              {formatRange(item.startDate, item.endDate)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
